"use client";

/**
 * Launch-time data catch-up (LOCAL install only).
 *
 * On the first mount per app session it asks /api/maintenance/catch-up whether
 * this is the local instance and whether any domain is behind. If so it kicks
 * off a right-sized catch-up (only the stale domains) and shows a small,
 * dismissible progress banner tailing the job's log. On the hosted VPS `local`
 * is false, so this is a no-op. Fully-current installs show nothing.
 */
import { useEffect, useState } from "react";

// Once per app session — not per route navigation.
let booted = false;

type Health = {
  report: {
    anyStale: boolean;
    domains: Array<{ key: string; label: string; behind: number; stale: boolean }>;
  };
  local: boolean;
  busy: string | null;
  catchup: { status: string; lastLine: string } | null;
};

export function CatchUpBoot() {
  const [phase, setPhase] = useState<"hidden" | "running" | "done" | "failed">("hidden");
  const [line, setLine] = useState("");
  const [summary, setSummary] = useState("");
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (booted) return;
    booted = true;
    let alive = true;
    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

    const poll = async () => {
      try {
        const d: Health = await (await fetch("/api/maintenance/catch-up")).json();
        if (!alive) return;
        const st = d.catchup?.status;
        if (st === "running" || d.busy === "catch-up") {
          setLine(d.catchup?.lastLine || "working…");
        } else if (st === "done") {
          stop(); setPhase("done");
          setTimeout(() => alive && setPhase("hidden"), 6000);
        } else if (st === "failed") {
          stop(); setPhase("failed");
        }
      } catch { /* transient */ }
    };

    (async () => {
      try {
        const d: Health = await (await fetch("/api/maintenance/catch-up")).json();
        if (!alive || !d.local) return; // VPS or unreachable → never auto-run
        if (d.busy === "catch-up" || d.catchup?.status === "running") {
          setPhase("running"); setLine(d.catchup?.lastLine || "working…");
          timer = setInterval(poll, 3000);
          return;
        }
        if (!d.report.anyStale || d.busy) return; // current, or another job holds the runner
        setSummary(d.report.domains.filter((x) => x.stale).map((x) => `${x.label} · ${x.behind} behind`).join("  ·  "));
        const post = await fetch("/api/maintenance/catch-up", { method: "POST" });
        if (!alive || !post.ok) return;
        setPhase("running"); setLine("starting…");
        timer = setInterval(poll, 3000);
      } catch { /* transient */ }
    })();

    return () => { alive = false; stop(); };
  }, []);

  if (phase === "hidden" || dismissed) return null;

  const tone =
    phase === "done" ? "ring-bull/30" : phase === "failed" ? "ring-bear/30" : "ring-accent/30";
  return (
    <div className={`fixed bottom-4 right-4 z-50 w-80 rounded-lg bg-surface p-3 text-xs shadow-panel-raised ring-1 ${tone}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 font-semibold text-ink">
            {phase === "running" && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />}
            {phase === "running" ? "Catching up your data…" : phase === "done" ? "Data up to date" : "Catch-up incomplete"}
          </div>
          {phase === "running" && summary && <div className="mt-0.5 truncate text-[11px] text-ink-muted">{summary}</div>}
          {phase === "running" && <div className="mt-1 truncate font-mono text-2xs text-ink-muted">{line}</div>}
          {phase === "done" && <div className="mt-0.5 text-[11px] text-bull">History and breadth are current.</div>}
          {phase === "failed" && <div className="mt-0.5 text-[11px] text-bear">See Settings → Data Maintenance for details.</div>}
        </div>
        <button onClick={() => setDismissed(true)} aria-label="Dismiss" className="shrink-0 rounded p-0.5 text-ink-faint hover:bg-surface-sunken hover:text-ink">✕</button>
      </div>
    </div>
  );
}
